import { forwardRef, useState } from "react";
import type { TreeItemComponentProps } from "dnd-kit-sortable-tree";
import { SimpleTreeItemWrapper } from "dnd-kit-sortable-tree";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { Icon } from "@circle-react-shared/Icon";
import { LayersTreeBlockName } from "../LayersTreeBlockName";
import { LayersTreeItemMenu } from "../LayersTreeItemMenu";
import type { TreeItem } from "../types";

export const LayersTreeItem = forwardRef<
  HTMLDivElement,
  TreeItemComponentProps<TreeItem>
  // eslint-disable-next-line @typescript-eslint/unbound-method -- False positive, arrow function
>(({ item, depth, clone, ghost, isOver, onCollapse, ...props }, ref) => {
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isExpanded = hasChildren && !item.collapsed;
  const buttonLabel = t([
    "builder_ui.layers",
    isExpanded ? "collapse_block" : "expand_block",
  ]);
  const { name, type, isEditable, hasHiddenAncestor, isHidden } = item;
  const layerName = name || type;

  return (
    <SimpleTreeItemWrapper
      {...props}
      ref={ref}
      item={item}
      depth={depth}
      clone={clone}
      ghost={ghost}
      isOver={isOver}
      // className is applied to the <li> element
      className={classNames(
        "hover:bg-secondary group relative text-sm font-medium transition-colors",
        {
          "!opacity-100": ghost,
        },
      )}
      // contentClassName is applied to the inner <div> element
      contentClassName={classNames("!border-0 !py-0 cursor-default", {
        "!p-0": ghost,
        "bg-secondary": clone,
      })}
      aria-level={depth}
      aria-grabbed={clone ? true : undefined}
      aria-dropeffect={isOver ? "move" : undefined}
      showDragHandle={false}
      disableCollapseOnItemClick
    >
      {ghost ? (
        <div className="bg-circle h-[2px] w-full" />
      ) : (
        <div
          className={classNames(
            "flex w-full min-w-0 items-center gap-0.5 text-left",
            {
              "opacity-50": isHidden || hasHiddenAncestor,
            },
          )}
          aria-expanded={isExpanded}
        >
          {hasChildren && (
            <button
              type="button"
              className="flex rounded-full"
              onClick={onCollapse}
              aria-label={buttonLabel}
              title={buttonLabel}
            >
              <Icon
                type="16-builder-ui-layers-chevron"
                size={16}
                className={classNames("!w-4 transition-all", {
                  "-rotate-90": !isExpanded,
                })}
              />
            </button>
          )}
          <LayersTreeBlockName
            blockId={item.id}
            type={item.type}
            depth={depth}
            name={layerName}
            isLayerNameEditable={isEditable}
            isBeingEdited={isBeingEdited}
          />
        </div>
      )}
      {!ghost && !clone && isEditable && (
        <LayersTreeItemMenu
          blockId={item.id}
          isHidden={item.isHidden}
          onClickEditName={() => setIsBeingEdited(true)}
        />
      )}
    </SimpleTreeItemWrapper>
  );
});

LayersTreeItem.displayName = "LayersTreeItem";
